import {
  Timestamp,
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  CHEERLEAD_MESSAGE_MAX,
  CheerleadNoteRecord,
  PARTNER_LABEL_MAX,
  PARTNER_NAME_MAX,
  PartnerLinkRecord,
  PartnerViewRecord,
} from '../types/partner';
import { TrackerSummaryRecord } from '../types/dailyBite';

/* ────────────────────────────────────────────────────────────────────────────
   Doc refs
   ──────────────────────────────────────────────────────────────────────── */

function partnerLinksCol(uid: string) {
  return collection(db, 'users', uid, 'partner_links');
}

function partnerLinkRef(uid: string, linkId: string) {
  return doc(db, 'users', uid, 'partner_links', linkId);
}

function partnerViewRef(linkId: string) {
  return doc(db, 'partner_views', linkId);
}

function cheerleadNotesCol(memberUid: string) {
  return collection(db, 'users', memberUid, 'cheerlead_notes');
}

/* ────────────────────────────────────────────────────────────────────────────
   LinkId generator — 20 chars of URL-safe entropy. Long enough that the
   capability-style access model isn't enumerable.
   ──────────────────────────────────────────────────────────────────────── */

function generateLinkId(): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
  const size = 20;
  const bytes = new Uint8Array(size);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < size; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  let out = '';
  for (let i = 0; i < size; i += 1) out += alphabet[bytes[i] % alphabet.length];
  return out;
}

/* ────────────────────────────────────────────────────────────────────────────
   Mappers
   ──────────────────────────────────────────────────────────────────────── */

function mapLink(id: string, data: Record<string, any>): PartnerLinkRecord {
  return {
    id,
    label: data.label || 'Partner',
    status: data.status === 'revoked' ? 'revoked' : 'active',
    createdAt: data.createdAt ?? null,
    revokedAt: data.revokedAt ?? null,
    lastCheerleadAt: data.lastCheerleadAt ?? null,
    cheerleadCount: typeof data.cheerleadCount === 'number' ? data.cheerleadCount : 0,
  };
}

function mapCheerlead(id: string, data: Record<string, any>): CheerleadNoteRecord {
  return {
    id,
    linkId: data.linkId || '',
    partnerName: data.partnerName || 'Your partner',
    message: data.message || '',
    sentAt: data.sentAt ?? null,
    read: Boolean(data.read),
  };
}

/* ────────────────────────────────────────────────────────────────────────────
   Member-side CRUD
   ──────────────────────────────────────────────────────────────────────── */

export interface CreatePartnerLinkInput {
  label: string;
  memberFirstName: string;
  summary: TrackerSummaryRecord;
}

export async function createPartnerLink(
  uid: string,
  input: CreatePartnerLinkInput,
): Promise<PartnerLinkRecord> {
  const label = (input.label || '').trim().slice(0, PARTNER_LABEL_MAX) || 'Partner';
  const memberFirstName = (input.memberFirstName || '').trim().slice(0, 40) || 'a friend';

  const linkId = generateLinkId();

  const view: PartnerViewRecord = {
    memberUid: uid,
    memberFirstName,
    status: 'active',
    currentStreak: input.summary.currentStreak || 0,
    longestStreak: input.summary.longestStreak || 0,
    badgesEarnedCount: input.summary.earnedBadges?.length || 0,
  };

  const batch = writeBatch(db);
  batch.set(partnerViewRef(linkId), {
    ...view,
    updatedAt: serverTimestamp(),
  });
  batch.set(partnerLinkRef(uid, linkId), {
    label,
    status: 'active',
    cheerleadCount: 0,
    createdAt: serverTimestamp(),
  });
  await batch.commit();

  return {
    id: linkId,
    label,
    status: 'active',
    createdAt: null,
    revokedAt: null,
    lastCheerleadAt: null,
    cheerleadCount: 0,
  };
}

export async function listPartnerLinks(uid: string): Promise<PartnerLinkRecord[]> {
  const snap = await getDocs(query(partnerLinksCol(uid), orderBy('createdAt', 'desc')));
  return snap.docs.map((d) => mapLink(d.id, d.data() as Record<string, any>));
}

export async function revokePartnerLink(uid: string, linkId: string): Promise<void> {
  const batch = writeBatch(db);
  batch.update(partnerLinkRef(uid, linkId), {
    status: 'revoked',
    revokedAt: serverTimestamp(),
  });
  batch.update(partnerViewRef(linkId), {
    status: 'revoked',
    updatedAt: serverTimestamp(),
  });
  await batch.commit();
}

/**
 * Keep every active `partner_views` doc for this member in sync with the
 * latest streak + badge state. Called from `recomputeAndSaveSummary` so
 * partners always see fresh numbers without us needing a Cloud Function.
 *
 * Fire-and-forget: failures shouldn't block the member's save path.
 */
export async function syncPartnerViewsFromSummary(
  uid: string,
  memberFirstName: string,
  summary: TrackerSummaryRecord,
): Promise<void> {
  const snap = await getDocs(
    query(partnerLinksCol(uid), where('status', '==', 'active')),
  );
  if (snap.empty) return;

  const batch = writeBatch(db);
  const payload = {
    memberUid: uid,
    memberFirstName: memberFirstName.trim().slice(0, 40) || 'a friend',
    status: 'active' as const,
    currentStreak: summary.currentStreak || 0,
    longestStreak: summary.longestStreak || 0,
    badgesEarnedCount: summary.earnedBadges?.length || 0,
    updatedAt: serverTimestamp(),
  };
  for (const d of snap.docs) {
    batch.set(partnerViewRef(d.id), payload, { merge: true });
  }
  await batch.commit();
}

/* ────────────────────────────────────────────────────────────────────────────
   Member-side cheerlead feed
   ──────────────────────────────────────────────────────────────────────── */

export async function listCheerleadNotes(
  uid: string,
  max: number = 20,
): Promise<CheerleadNoteRecord[]> {
  const snap = await getDocs(
    query(cheerleadNotesCol(uid), orderBy('sentAt', 'desc'), limit(max)),
  );
  return snap.docs.map((d) => mapCheerlead(d.id, d.data() as Record<string, any>));
}

export async function markCheerleadRead(uid: string, noteId: string): Promise<void> {
  await updateDoc(doc(cheerleadNotesCol(uid), noteId), { read: true });
}

/* ────────────────────────────────────────────────────────────────────────────
   Partner-side (unauthenticated) operations
   ──────────────────────────────────────────────────────────────────────── */

export async function getPartnerView(linkId: string): Promise<PartnerViewRecord | null> {
  const snap = await getDoc(partnerViewRef(linkId));
  if (!snap.exists()) return null;
  const data = snap.data() as Record<string, any>;
  return {
    memberUid: data.memberUid,
    memberFirstName: data.memberFirstName || 'a friend',
    status: data.status === 'revoked' ? 'revoked' : 'active',
    currentStreak: data.currentStreak || 0,
    longestStreak: data.longestStreak || 0,
    badgesEarnedCount: data.badgesEarnedCount || 0,
    updatedAt: data.updatedAt ?? null,
  };
}

export interface SendCheerleadInput {
  linkId: string;
  memberUid: string;
  partnerName: string;
  message: string;
}

export async function sendCheerleadNote(input: SendCheerleadInput): Promise<void> {
  const partnerName = (input.partnerName || '').trim().slice(0, PARTNER_NAME_MAX) || 'Your partner';
  const message = (input.message || '').trim().slice(0, CHEERLEAD_MESSAGE_MAX);
  if (!message) throw new Error('Write a short note before sending.');

  await addDoc(cheerleadNotesCol(input.memberUid), {
    linkId: input.linkId,
    partnerName,
    message,
    read: false,
    sentAt: serverTimestamp(),
  });
}

/**
 * After a partner sends a cheerlead, bump the counter on the member's own
 * partner_link record. Best-effort — the partner is unauthenticated and
 * can't write to the member's private subtree, so this runs from the
 * member's client next time they open the app. See listCheerleadNotes.
 */
export async function reconcileCheerleadCounts(uid: string): Promise<void> {
  const [linksSnap, notesSnap] = await Promise.all([
    getDocs(partnerLinksCol(uid)),
    getDocs(cheerleadNotesCol(uid)),
  ]);
  const countsByLink = new Map<string, { count: number; lastAt: Timestamp | null }>();
  for (const n of notesSnap.docs) {
    const data = n.data() as Record<string, any>;
    const linkId = data.linkId as string;
    if (!linkId) continue;
    const existing = countsByLink.get(linkId) || { count: 0, lastAt: null as Timestamp | null };
    existing.count += 1;
    const at = data.sentAt as Timestamp | undefined;
    if (at && (!existing.lastAt || at.toMillis() > existing.lastAt.toMillis())) {
      existing.lastAt = at;
    }
    countsByLink.set(linkId, existing);
  }

  const batch = writeBatch(db);
  let dirty = false;
  for (const d of linksSnap.docs) {
    const data = d.data() as Record<string, any>;
    const next = countsByLink.get(d.id) || { count: 0, lastAt: null };
    if ((data.cheerleadCount || 0) !== next.count || (data.lastCheerleadAt ?? null) !== next.lastAt) {
      batch.update(partnerLinkRef(uid, d.id), {
        cheerleadCount: next.count,
        lastCheerleadAt: next.lastAt,
      });
      dirty = true;
    }
  }
  if (dirty) await batch.commit();
}

/* ────────────────────────────────────────────────────────────────────────────
   URL helper
   ──────────────────────────────────────────────────────────────────────── */

export function buildCheerUrl(linkId: string): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/cheer/${linkId}`;
  }
  return `https://meliohealthmgmt.co.uk/cheer/${linkId}`;
}
